// Throttles rapid, repeated "Add to cart" submissions.
//
// Shopify's storefront bot detection has been observed rejecting cart
// mutation requests (HTTP 429, "Verifying your connection...") when the
// same request pattern repeats in a short window from one session —
// Shopify staff describe this as expected/by-design behavior, not a bug:
// https://community.shopify.dev/t/aggressive-bot-detection-with-429-errors-ajax-cart-api/28900
// This can't remove that risk (only Shopify controls the threshold), it
// only spaces out consecutive successful "add" submissions from this
// browser so they look less like a script hammering the endpoint.
//
// Two layers, both scoped to `requestType === 'add'` only (quantity
// changes in the cart drawer are a separate, lower-frequency pattern and
// weren't part of the reported problem):
// 1. A functional guard on window.liquidAjaxCart.add itself — every
//    "Add to cart" entry point in the theme (PDP, product cards, quick
//    view, the sticky buy bar) calls this same function, so wrapping it
//    once here covers all of them without touching each template.
// 2. A CSS-only visual lock (a class on <html>) so the button doesn't look
//    broken while ignoring clicks — deliberately not touching any
//    button's own `disabled` property, which stays owned by the theme's
//    existing sold-out/no-variant-selected/mid-request logic.
(() => {
  const COOLDOWN_MS = 0;
  const COOLDOWN_ATTR = 'data-cart-add-cooldown';

  let cooldownUntil = 0;
  let cooldownTimer = null;

  function lockUI() {
    document.documentElement.setAttribute(COOLDOWN_ATTR, '');
    clearTimeout(cooldownTimer);
    cooldownTimer = setTimeout(() => {
      document.documentElement.removeAttribute(COOLDOWN_ATTR);
    }, COOLDOWN_MS);
  }

  document.addEventListener('liquid-ajax-cart:request-end', (event) => {
    const { requestState } = event.detail;
    if (requestState.requestType !== 'add') return;
    if (!requestState.responseData?.ok) return; // a failed add can be retried immediately

    cooldownUntil = Date.now() + COOLDOWN_MS;
    lockUI();
  });

  document.addEventListener('liquid-ajax-cart:init', () => {
    const originalAdd = window.liquidAjaxCart.add;
    window.liquidAjaxCart.add = function (...args) {
      if (Date.now() < cooldownUntil) return;
      return originalAdd.apply(window.liquidAjaxCart, args);
    };
  }, { once: true });
})();
